/**
 * The company's name correction: the read the page shows. Every action ends with
 * `refreshCustomerQuery(EIN_CORRECTION_CACHE_KEY)`, so the page (and the sidebar, which reads the
 * same request) always shows what the API says, not what it guessed.
 */
import { useCustomerQuery } from '@/hooks/useCustomerQuery'

import { EIN_CORRECTION_CACHE_KEY, fetchEinCorrection, hasEinCorrectionToReturnTo } from './read'
import type { EinCorrectionLanding } from './types'

/**
 * `companyId` undefined (no company in the store yet) reads nothing. The page revalidates on mount;
 * the sidebar passes `revalidateOnMount: false` and reads the cached entry.
 */
export function useEinCorrection(companyId: string | undefined, { revalidateOnMount = true } = {}) {
  return useCustomerQuery<EinCorrectionLanding>({
    cacheKey: EIN_CORRECTION_CACHE_KEY,
    scopeKey: companyId ?? null,
    queryFn: () => fetchEinCorrection(companyId as string),
    revalidateOnMount,
    enabled: Boolean(companyId),
  })
}

/**
 * Whether the sidebar lists "Name correction" under Services: the company has a request that is not
 * cancelled. One cached read per company (the page's cache entry), never a revalidation per page;
 * a failed read lists nothing. `companyId` undefined reads nothing.
 */
export function useHasEinCorrection(companyId: string | undefined): boolean {
  return hasEinCorrectionToReturnTo(useEinCorrection(companyId, { revalidateOnMount: false }).data)
}
